import { PROMO_CODES, type PromoCode } from './codes'
import { promo } from './store'

/**
 * The words of the site's sign-up modal, for the visitor's code.
 *
 * The modal is Webflow's, written for WELCOME40. Its heading, sentence and
 * button each carry another code's words in an attribute named for that code,
 * such as `data-cv-promo-firstfree="Get your first month free!"`, and the
 * bundle puts them in place for a visitor offered that code. Webflow's own
 * words stay for every other code, and show if the bundle doesn't load. So the
 * words live in Webflow, where they're edited and styled, and changing them
 * takes only a Webflow publish.
 */

/** The attribute that carries `code`'s words: `data-cv-promo-firstfree`. */
export function promoWordsAttr(code: PromoCode): string {
  return `data-cv-promo-${code.toLowerCase()}`
}

const SELECTOR = PROMO_CODES.map((code) => `[${promoWordsAttr(code)}]`).join(',')

/** Each element's words as Webflow wrote them, to put back for a code it has none for. */
const webflowWords = new WeakMap<Element, string>()

/** A submit button built as an `<input>` shows its value; anything else, its text. */
function isInput(element: Element): element is HTMLInputElement {
  return element.tagName === 'INPUT'
}

function wordsOf(element: Element): string {
  return isInput(element) ? element.value : (element.textContent ?? '')
}

/**
 * Put `code`'s words on every element under `root` that has some, and
 * Webflow's own back on any that has none for it. An element whose words are
 * already right is left alone, so the page's MutationObserver, which calls
 * this for every change, settles at once.
 */
export function applyPromoWords(
  root: ParentNode = document,
  code: PromoCode = promo.get().code,
): void {
  for (const element of root.querySelectorAll(SELECTOR)) {
    if (!webflowWords.has(element)) webflowWords.set(element, wordsOf(element))
    const words = element.getAttribute(promoWordsAttr(code)) ?? webflowWords.get(element) ?? ''
    if (wordsOf(element) === words) continue
    if (isInput(element)) element.value = words
    else element.textContent = words
  }
}

/**
 * Word the page for the visitor's code now, and again whenever it changes: a
 * sign-up through the other code's form, or one in another tab.
 */
export function watchPromoWords(): () => void {
  applyPromoWords()
  return promo.subscribe((state, previous) => {
    if (state.code !== previous.code) applyPromoWords(document, state.code)
  })
}
